// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Button, ImprCheckbox, Select } from '@imprivata-cloud/components';
import { Alert } from '@imprivata-cloud/design-system';
import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import './Security.less';
import { UndoOutlined } from '@ant-design/icons';
import { Entitlement } from '@api/constants';
import type { RbaPolicySetRequest } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { ROLE_TYPE_ADMIN } from '@portal/constants';
import { AuthMethodsPanel } from '@portal/containers/security/AuthMethodsPanel';
import {
  AuthnLevel,
  FieldNames,
  type FormValues,
  InstanceKey,
} from '@portal/containers/security/constants';
import { ConditionsSummary } from '@portal/containers/security/conditions/ConditionsSummary';
import { RiskAwarenessSection } from '@portal/containers/security/conditions/RiskAwarenessSection';
import { APPLICATION_LABEL_KEYS } from '@portal/containers/security/conditions/labels';
import { useRiskLevelOptions } from '@portal/containers/security/conditions/riskLevels';
import { useRuleSets } from '@portal/containers/security/conditions/useRules';
import {
  buildHubAccessSettings,
  isAccountRecoveryEnabled,
} from '@portal/containers/security/useConfigSettings';
import { useRbaPolicyMutation, useRbaPolicyQuery } from '@portal/containers/security/useRbaPolicy';
import { useSecurityData } from '@portal/containers/security/useSecurityData';
import { useUnsavedChangesPrompt } from '@portal/hooks/useUnsavedChangesPrompt';
import { featureSupported } from '@/featureFlagsUtils';

const TRANSLATION = 'portal.security';

// The two applications whose authentication strength the conditions editor owns, in the order the
// page has always listed them. `as const` rather than `InstanceKey[]`: the labels are looked up in a
// map covering exactly these two, so widening the array to the whole enum would make that lookup a
// partial one the compiler has to be argued out of.
const CONDITIONS_APPLICATIONS = [InstanceKey.USS, InstanceKey.IAM] as const;

const authnLevelToLabelKey = (value: string): string => {
  switch (value) {
    case AuthnLevel.ICP_SINGLE_FA:
      return `${TRANSLATION}.options.ICP_SINGLE_FA`;
    case AuthnLevel.ICP_MFA:
      return `${TRANSLATION}.options.ICP_MFA`;
    case AuthnLevel.ICP_PASSWORDLESS_MFA:
      return `${TRANSLATION}.options.ICP_PASSWORDLESS_MFA`;
    default:
      return value;
  }
};

function Security() {
  const { t } = useTranslation();
  const { tenantInfo } = useSession();
  const riskAwarenessEntitled = hasEntitlement(tenantInfo, Entitlement.RISK_BASED_ACCESS);
  // Read once. Every branch below is this one boolean, so the two projections of authentication
  // strength cannot both render and cannot both be absent.
  const conditionsEditor = featureSupported('RISK_AWARENESS_ENABLED');

  const riskPolicyActive = conditionsEditor && riskAwarenessEntitled;
  const {
    data: rbaPolicy,
    error: rbaPolicyError,
    isLoading: isLoadingRbaPolicy,
    refetch: refetchRbaPolicy,
  } = useRbaPolicyQuery({ enabled: riskPolicyActive });
  const { mutateAsync: saveRbaPolicy, isPending: isSavingRbaPolicy } = useRbaPolicyMutation();
  const savedRiskAwarenessOn = rbaPolicy?.rbaEnabled ?? false;
  const savedRuleSetId = rbaPolicy?.ruleSet?.id ?? null;
  const [riskAwarenessDraft, setRiskAwarenessDraft] = useState<boolean>();
  const [ruleSetIdDraft, setRuleSetIdDraft] = useState<string | null>();
  const riskAwarenessOn = riskAwarenessDraft ?? savedRiskAwarenessOn;
  const { data: ruleSetsData } = useRuleSets(conditionsEditor);
  const firstRuleSetId = riskAwarenessOn ? (ruleSetsData?.[0]?.id ?? null) : null;
  const ruleSetId = ruleSetIdDraft === undefined ? (savedRuleSetId ?? firstRuleSetId) : ruleSetIdDraft;

  const riskLevelOptions = useRiskLevelOptions();
  const conditionControlsDirty =
    riskAwarenessOn !== savedRiskAwarenessOn || ruleSetId !== savedRuleSetId;
  const ruleSetName =
    ruleSetsData?.find(rs => rs.id === ruleSetId)?.name ??
    (rbaPolicy?.ruleSet?.id === ruleSetId ? rbaPolicy.ruleSet?.name : undefined);
  const riskPolicyIncomplete =
    riskPolicyActive && riskAwarenessOn && (ruleSetId === null || ruleSetName === undefined);

  const { emitError, clearErrorNotifications } = useErrorContext();
  // Until the saved policy has loaded, "off" is a guess, not the tenant's setting; editing it
  // would let a save overwrite a policy that is really on.
  const riskPolicyUnavailable = riskPolicyActive && (isLoadingRbaPolicy || rbaPolicyError !== null);
  useEffect(() => {
    if (rbaPolicyError) {
      emitError(new AppError(rbaPolicyError));
    }
  }, [rbaPolicyError, emitError]);
  const [form] = Form.useForm();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const hasUnsavedChanges = isFormDirty || conditionControlsDirty;
  const blocker = useBlocker(hasUnsavedChanges);
  useUnsavedChangesPrompt({ hasUnsavedChanges });
  const dismissUnsavedAlert = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };
  const ussSelected = Form.useWatch(FieldNames.USS_VALUE, form);
  const iamSelected = Form.useWatch(FieldNames.IAM_VALUE, form);

  const {
    ussAuthnStrength,
    iamAuthnStrength,
    ussAuthnStrengthError,
    iamAuthnStrengthError,
    ussSetting,
    iamSetting,
    ussError,
    iamError,
    accountRecoveryEnabledSetting,
    accountRecoveryEnabledError,
    isLoading,
    refetchAll,
    syncAuthnStrengthCache,
    updateSettings,
  } = useSecurityData();

  // Populate a dropdown + one-factor checkbox pair from the server's current
  // authentication-strength value. Takes the value (not the full response) so
  // the populating effects can depend on it alone and avoid re-running when
  // unrelated parts of the response change.
  const applyAuthnStrengthToForm = useCallback(
    (currentValue: string | undefined, valueField: FieldNames, oneFactorField: FieldNames) => {
      if (!currentValue) {
        return;
      }
      const isMfaIfEnrolled = currentValue === AuthnLevel.ICP_MFA_IF_ENROLLED;
      form.setFieldsValue({
        [valueField]: (isMfaIfEnrolled ? AuthnLevel.ICP_MFA : currentValue) as AuthnLevel,
        [oneFactorField]: isMfaIfEnrolled,
      });
    },
    [form],
  );

  useEffect(() => {
    applyAuthnStrengthToForm(
      ussAuthnStrength?.currentValue,
      FieldNames.USS_VALUE,
      FieldNames.USS_ONE_FACTOR_VALUE,
    );
  }, [ussAuthnStrength?.currentValue, applyAuthnStrengthToForm]);

  // Populate IAM form from authentication-strength currentValue
  useEffect(() => {
    applyAuthnStrengthToForm(
      iamAuthnStrength?.currentValue,
      FieldNames.IAM_VALUE,
      FieldNames.IAM_ONE_FACTOR_VALUE,
    );
  }, [iamAuthnStrength?.currentValue, applyAuthnStrengthToForm]);

  // What the loaded settings say each server-owned field should hold. Populate-on-load and
  // undo both apply this one object, so a field cannot be reverted by one and forgotten by
  // the other.
  const serverFieldValues = useMemo(
    () => ({
      [FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE]: isAccountRecoveryEnabled(
        accountRecoveryEnabledSetting,
      ),
    }),
    [accountRecoveryEnabledSetting],
  );

  // Undo reads the values through a ref rather than its own closure. react-query's
  // structural sharing returns the same reference when a refetch produces unchanged data,
  // so this effect may not re-run at all after a refetch; when it does, it runs after undo
  // has already resumed. Both orderings settle on the values the server last reported.
  const serverFieldValuesRef = useRef(serverFieldValues);

  // Populate the server-owned fields from the loaded settings
  useEffect(() => {
    serverFieldValuesRef.current = serverFieldValues;
    form.setFieldsValue(serverFieldValues);
  }, [serverFieldValues, form]);

  const onFormChanges = (changedValues: Partial<FormValues>) => {
    setIsFormDirty(true);
    if (
      changedValues[FieldNames.USS_VALUE] &&
      changedValues[FieldNames.USS_VALUE] !== AuthnLevel.ICP_MFA
    ) {
      form.setFieldValue(FieldNames.USS_ONE_FACTOR_VALUE, false);
    }

    if (
      changedValues[FieldNames.IAM_VALUE] &&
      changedValues[FieldNames.IAM_VALUE] !== AuthnLevel.ICP_MFA
    ) {
      form.setFieldValue(FieldNames.IAM_ONE_FACTOR_VALUE, false);
    }
  };

  // Save writes to two independent endpoints. Each is attempted and reports its own failure, but the
  // form stays dirty unless both landed: clearing it early would disable Save and Undo and strand the
  // form showing a value the tenant does not actually have.
  async function onFinish(formValues: FormValues) {
    dismissUnsavedAlert();
    clearErrorNotifications();
    let anySaveFailed = false;
    const attempt = (save: Promise<unknown>) =>
      save.catch((e: ApiError) => {
        anySaveFailed = true;
        emitError(new AppError(e));
      });

    const settings = buildHubAccessSettings(
      { ussSetting, iamSetting, accountRecoveryEnabledSetting },
      {
        authnStrength: conditionsEditor ? null : formValues,
        accountRecovery: formValues[FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE],
      },
    );
    const saves = [
      attempt(
        updateSettings(settings).then(async () => {
          if (!conditionsEditor) {
            syncAuthnStrengthCache(formValues);
          }
          await refetchAll();
        }),
      ),
    ];

    if (riskPolicyActive && !riskPolicyUnavailable && conditionControlsDirty) {
      const request: RbaPolicySetRequest =
        riskAwarenessOn && ruleSetId !== null && ruleSetName !== undefined
          ? { rbaEnabled: true, ruleSetId, ruleSetName }
          : { rbaEnabled: false };
      saves.push(
        attempt(
          saveRbaPolicy(request).then(() => {
            setRiskAwarenessDraft(undefined);
            setRuleSetIdDraft(undefined);
          }),
        ),
      );
    }

    await Promise.all(saves);
    if (!anySaveFailed) {
      setIsFormDirty(false);
    }
  }

  async function handleUndo() {
    dismissUnsavedAlert();
    clearErrorNotifications();
    const refetches: Promise<unknown>[] = [refetchAll()];
    if (riskPolicyActive) {
      refetches.push(refetchRbaPolicy());
    }
    setRiskAwarenessDraft(undefined);
    setRuleSetIdDraft(undefined);
    await Promise.all(refetches);
    applyAuthnStrengthToForm(
      ussAuthnStrength?.currentValue,
      FieldNames.USS_VALUE,
      FieldNames.USS_ONE_FACTOR_VALUE,
    );
    applyAuthnStrengthToForm(
      iamAuthnStrength?.currentValue,
      FieldNames.IAM_VALUE,
      FieldNames.IAM_ONE_FACTOR_VALUE,
    );
    // react-query's structural sharing keeps the same object reference when a refetch
    // returns unchanged data, so the populate-on-load effect keyed on that reference
    // won't re-fire — reapply the server values here, matching the USS/IAM revert above.
    form.setFieldsValue(serverFieldValuesRef.current);
    setIsFormDirty(false);
  }

  const ussFilteredOptions = useMemo(
    () =>
      (ussAuthnStrength?.availableValues ?? [])
        .filter(value => value !== AuthnLevel.ICP_MFA_IF_ENROLLED)
        .map(value => ({ value, label: t(authnLevelToLabelKey(value)) })),
    [ussAuthnStrength?.availableValues, t],
  );
  const iamFilteredOptions = useMemo(
    () =>
      (iamAuthnStrength?.availableValues ?? [])
        .filter(value => value !== AuthnLevel.ICP_MFA_IF_ENROLLED)
        .map(value => ({ value, label: t(authnLevelToLabelKey(value)) })),
    [iamAuthnStrength?.availableValues, t],
  );

  const ussOptionsBroken = !!ussAuthnStrengthError || ussFilteredOptions.length === 0;
  const iamOptionsBroken = !!iamAuthnStrengthError || iamFilteredOptions.length === 0;
  // A failed read leaves the checkbox at its initial `true`, which must not be saved as the tenant's value.
  const accountRecoveryUnknown = !!accountRecoveryEnabledError;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={onFormChanges}
      className="security-container"
      initialValues={{ [FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE]: true }}
    >
      <section className="security-card security-access">
        <div className="security-column">
          {blocker.state === 'blocked' && hasUnsavedChanges && (
            <Alert
              className="security-unsaved-alert"
              status="warning"
              title={t(`${TRANSLATION}.unsaved-changes.title`)}
              message={t(`${TRANSLATION}.unsaved-changes.message`)}
              onClose={blocker.reset}
            />
          )}
          <p className="security-for-org-message">{t(`${TRANSLATION}.description`)}</p>
          {conditionsEditor ? (
            <>
              {riskPolicyActive && (
                <RiskAwarenessSection
                  isOn={riskAwarenessOn}
                  ruleSetId={ruleSetId}
                  disabled={riskPolicyUnavailable}
                  onToggle={setRiskAwarenessDraft}
                  onRuleSetChange={setRuleSetIdDraft}
                />
              )}
              {CONDITIONS_APPLICATIONS.map(instanceKey => (
                <section className="conditions-section" key={instanceKey}>
                  <h2 className="conditions-application">
                    {t(APPLICATION_LABEL_KEYS[instanceKey])}
                  </h2>
                  <ConditionsSummary
                    instanceKey={instanceKey}
                    riskAwarenessOn={riskAwarenessOn}
                    riskAwarenessEnabled={conditionsEditor}
                    conditionControlsDirty={conditionControlsDirty}
                    entitled={riskAwarenessEntitled}
                    riskLevels={riskLevelOptions}
                  />
                </section>
              ))}
            </>
          ) : (
            <>
              <Form.Item name={FieldNames.USS_VALUE}>
                <Select
                  label={t(`${TRANSLATION}.uss-field-label`)}
                  autoFocus
                  options={ussFilteredOptions}
                  disabled={isLoading || ussOptionsBroken}
                />
              </Form.Item>
              <Form.Item name={FieldNames.USS_ONE_FACTOR_VALUE} valuePropName="checked">
                {/*  When checked, server value must be icp-mfa-if-enrolled */}
                <ImprCheckbox disabled={ussSelected !== AuthnLevel.ICP_MFA}>
                  {t(`${TRANSLATION}.checkbox-label`)}
                </ImprCheckbox>
              </Form.Item>

              <Form.Item name={FieldNames.IAM_VALUE}>
                <Select
                  label={t(`${TRANSLATION}.iam-field-label`)}
                  options={iamFilteredOptions}
                  disabled={isLoading || iamOptionsBroken}
                />
              </Form.Item>
              <Form.Item name={FieldNames.IAM_ONE_FACTOR_VALUE} valuePropName="checked">
                {/*  When checked, server value must be icp-mfa-if-enrolled */}
                <ImprCheckbox disabled={iamSelected !== AuthnLevel.ICP_MFA}>
                  {t(`${TRANSLATION}.checkbox-label`)}
                </ImprCheckbox>
              </Form.Item>
            </>
          )}

          <hr />

          <Form.Item
            name={FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE}
            valuePropName="checked"
            className="account-recovery-field__checkbox"
          >
            <ImprCheckbox
              disabled={isLoading || accountRecoveryUnknown}
              aria-describedby="account-recovery-helper-text"
            >
              {t(`${TRANSLATION}.account-recovery.checkbox-label`)}
            </ImprCheckbox>
          </Form.Item>
          <p id="account-recovery-helper-text" className="account-recovery-field__description">
            {t(`${TRANSLATION}.account-recovery.helper-text`)}
          </p>
        </div>
        <AuthMethodsPanel />
      </section>

      <div className="security-card buttons-container">
        <Button
          type="text"
          className="text-button"
          onClick={handleUndo}
          disabled={!hasUnsavedChanges}
          icon={<UndoOutlined className="undo-icon" />}
        >
          {t('common.undo')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          disabled={
            isLoading ||
            isSavingRbaPolicy ||
            riskPolicyIncomplete ||
            !hasUnsavedChanges ||
            accountRecoveryUnknown ||
            (!conditionsEditor &&
              (ussOptionsBroken || iamOptionsBroken || !!ussError || !!iamError))
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </Form>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Security);
